/**
 * ArInvoiceVoucher — Printable transaction voucher for AR Invoices.
 *
 * Rendered inside MasterReportPreview (which handles the print modal,
 * @media print isolation, and the Print button).
 */

import type { ReactNode } from 'react';
import type { ArInvoiceDto, ArInvoiceLineDto } from '@/types/arInvoice.types';
import { PrintableTable } from '@/components/master/report/PrintableTable';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function fmtNum(n: number | null | undefined, dec = 2): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LabelValue = ({ label, value }: { label: string; value: ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.6 }}>
    <span style={{ color: '#6b7280', minWidth: '120px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const colour: Record<string, { bg: string; text: string }> = {
    Draft:    { bg: '#f3f4f6', text: '#374151' },
    Submitted:{ bg: '#dbeafe', text: '#1e40af' },
    Approved: { bg: '#d1fae5', text: '#065f46' },
    Rejected: { bg: '#fee2e2', text: '#991b1b' },
    Posted:   { bg: '#a7f3d0', text: '#064e3b' },
    Cancelled:{ bg: '#f3f4f6', text: '#6b7280' },
    Reversed: { bg: '#ede9fe', text: '#5b21b6' },
  };
  const c = colour[status] ?? { bg: '#f3f4f6', text: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface ArInvoiceVoucherProps {
  invoice:     ArInvoiceDto;
  companyName: string;
  printedBy?:  string;
}

export const ArInvoiceVoucher = ({
  invoice,
  companyName,
  printedBy,
}: ArInvoiceVoucherProps): ReactNode => {

  const lineColumns = [
    { key: 'lineNumber', label: '#',           width: '28px',  align: 'center' as const,
      render: (l: ArInvoiceLineDto) => l.lineNumber },
    { key: 'accountCode', label: 'Account',    width: '90px',
      render: (l: ArInvoiceLineDto) => (
        <span>
          <span style={{ fontFamily: 'monospace', fontSize: '9px', color: '#2563eb', marginRight: '4px' }}>
            {l.accountCode}
          </span>
          <span style={{ color: '#6b7280', fontSize: '9px' }}>{l.accountName}</span>
        </span>
      )},
    { key: 'description', label: 'Description',
      render: (l: ArInvoiceLineDto) => l.description || '' },
    { key: 'quantity',    label: 'Qty',         width: '50px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => fmtNum(l.quantity, 2) },
    { key: 'unitPrice',   label: 'Unit Price',  width: '80px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => fmtNum(l.unitPrice) },
    { key: 'discountPct', label: 'Disc %',      width: '52px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => l.discountPercent > 0 ? fmtNum(l.discountPercent) + '%' : '' },
    { key: 'lineAmount',  label: 'Net Amt',     width: '80px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => fmtNum(l.lineAmount) },
    { key: 'taxRate',     label: 'Tax %',       width: '52px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => l.taxRate > 0 ? fmtNum(l.taxRate) + '%' : '' },
    { key: 'totalAmount', label: 'Total',       width: '84px',  align: 'right' as const,
      render: (l: ArInvoiceLineDto) => <strong>{fmtNum(l.totalAmount)}</strong> },
  ];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>

      {/* ── Voucher Header ──────────────────────────────────────────────────── */}
      <div style={{
        borderBottom: '2px solid #1e3a5f',
        paddingBottom: '12px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
              myLedger ONE
            </div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e3a5f', lineHeight: 1.2 }}>
              {invoice.isReversal ? 'Credit Note' : 'Tax Invoice'}
            </div>
            <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
              {companyName}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
              {invoice.invoiceNumber}
            </div>
            <div style={{ marginTop: '4px' }}>
              <StatusBadge status={invoice.workflowStatus} />
            </div>
            <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px' }}>
              Printed: {new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
              {printedBy && ` · ${printedBy}`}
            </div>
          </div>
        </div>
      </div>

      {/* ── Customer + Document details ─────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 32px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Bill To
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{invoice.customerName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{invoice.customerCode}</div>
          {invoice.reference && (
            <div style={{ fontSize: '10px', color: '#374151', marginTop: '4px' }}>
              <span style={{ color: '#6b7280' }}>Your Ref:</span> {invoice.reference}
            </div>
          )}
        </div>
        <div>
          <LabelValue label="Invoice Date"   value={fmtDate(invoice.invoiceDate)} />
          <LabelValue label="Posting Date"   value={fmtDate(invoice.postingDate)} />
          {invoice.dueDate && (
            <LabelValue label="Due Date"     value={fmtDate(invoice.dueDate)} />
          )}
          <LabelValue label="Currency"       value={`${invoice.currencyCode} (Rate: ${invoice.exchangeRate.toFixed(4)})`} />
          <LabelValue label="Period"         value={`${invoice.financialPeriodCode} / ${invoice.financialYearCode}`} />
        </div>
      </div>

      {/* Narration */}
      {invoice.narration && (
        <div style={{
          marginBottom: '16px',
          padding: '8px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#374151',
        }}>
          <span style={{ color: '#6b7280', fontWeight: 600 }}>Narration: </span>
          {invoice.narration}
        </div>
      )}

      {/* ── Lines table ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Invoice Lines
      </div>

      <PrintableTable
        columns={lineColumns}
        rows={invoice.lines ?? []}
        rowKey={(l) => l.id}
      />

      {/* ── Totals ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '4px',
        marginBottom: '20px',
      }}>
        <div style={{ width: '200px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>Sub Total:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(invoice.subTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '10px' }}>
            <span style={{ color: '#6b7280' }}>Tax:</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(invoice.taxAmount)}</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '5px 8px',
            marginTop: '4px',
            background: '#1e3a5f',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: 'white',
          }}>
            <span>Total ({invoice.currencyCode}):</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtNum(invoice.totalAmount)}</span>
          </div>
          {invoice.exchangeRate !== 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '9px', color: '#6b7280', marginTop: '2px' }}>
              <span>Base Amount:</span>
              <span style={{ fontFamily: 'monospace' }}>{fmtNum(invoice.baseAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Signature blocks ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '16px',
        marginTop: '32px',
        borderTop: '1px solid #e5e7eb',
        paddingTop: '16px',
      }}>
        {[
          { label: 'Prepared By', value: '' },
          { label: 'Approved By', value: '' },
          { label: 'Authorised By', value: '' },
        ].map(({ label, value }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              paddingTop: '4px',
              fontSize: '9px',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {label}
            </div>
            {value && (
              <div style={{ fontSize: '10px', color: '#374151', marginTop: '2px' }}>{value}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
